import { PlaysTab } from './PlaysTab'
import { FormationsTab } from './FormationsTab'
import { ConceptCategoryTab, CONCEPT_CATEGORY_TABS } from './ConceptCategoryTab'
import { ModifiersTab } from './ModifiersTab'
import { EDITOR_TAB_OPTIONS } from './Types'
import type { EditorTab, ViewMode } from './hooks/usePlaybookEditorState'
import type { Formation } from '@/types/Concept.types'
import type { Play } from '@/hooks/UsePlaybookData'

interface PlaybookEditorTabsProps {
	// Active tab state
	activeTab: EditorTab
	onTabChange: (tab: EditorTab) => void

	// Playbook ID
	playbookId: string

	// Plays tab props
	plays: Play[]
	trashedPlays: Play[]
	viewMode: ViewMode
	searchQuery: string
	selectedPlays: Set<string>
	isTrashView: boolean
	onNewPlay: () => void
	onOpenPlay: (playId: string) => void
	onAnimatePlay: (playId: string) => void
	onRenamePlay: (playId: string) => void
	onDeletePlay: (playId: string) => void
	onRestorePlay: (playId: string) => Promise<void>
	onDuplicatePlay: (playId: string) => Promise<void>
	onTogglePlaySelection: (playId: string) => void
	onSelectAllVisible: () => void
	onTrashToggle: () => void
	onBatchDelete: () => void
	onBatchRestore: () => void
	onBatchPermanentDelete: () => void
	onClearSelection: () => void
	onLoadMorePlays: () => Promise<void>
	hasMorePlays: boolean
	isLoadingPlays: boolean

	// Formations tab props
	formations: Formation[]
	onNewFormation: () => void
	onEditFormation: (formation: Formation) => void
	onDeleteFormation: (id: string) => void
	onDuplicateFormation: (id: string) => Promise<void>
	onLoadMoreFormations: () => Promise<void>
	hasMoreFormations: boolean
	isLoadingFormations: boolean

	// Concept category tab props
	onDeleteConcept: (id: string) => void
}

export function PlaybookEditorTabs({
	activeTab,
	onTabChange,
	playbookId,
	plays,
	trashedPlays,
	viewMode,
	searchQuery,
	selectedPlays,
	isTrashView,
	onNewPlay,
	onOpenPlay,
	onAnimatePlay,
	onRenamePlay,
	onDeletePlay,
	onRestorePlay,
	onDuplicatePlay,
	onTogglePlaySelection,
	onSelectAllVisible,
	onTrashToggle,
	onBatchDelete,
	onBatchRestore,
	onBatchPermanentDelete,
	onClearSelection,
	onLoadMorePlays,
	hasMorePlays,
	isLoadingPlays,
	formations,
	onNewFormation,
	onEditFormation,
	onDeleteFormation,
	onDuplicateFormation,
	onLoadMoreFormations,
	hasMoreFormations,
	isLoadingFormations,
	onDeleteConcept,
}: PlaybookEditorTabsProps) {
	if (activeTab === 'plays') {
		return (
			<PlaysTab
				playbookId={playbookId}
				plays={plays}
				trashedPlays={trashedPlays}
				viewMode={viewMode}
				searchQuery={searchQuery}
				selectedPlays={selectedPlays}
				isTrashView={isTrashView}
				activeTab={activeTab}
				onTabChange={onTabChange}
				tabOptions={EDITOR_TAB_OPTIONS}
				onNewPlay={onNewPlay}
				onOpenPlay={onOpenPlay}
				onAnimatePlay={onAnimatePlay}
				onRenamePlay={onRenamePlay}
				onDeletePlay={onDeletePlay}
				onRestorePlay={onRestorePlay}
				onDuplicatePlay={onDuplicatePlay}
				onTogglePlaySelection={onTogglePlaySelection}
				onSelectAllVisible={onSelectAllVisible}
				onTrashToggle={onTrashToggle}
				onBatchDelete={onBatchDelete}
				onBatchRestore={onBatchRestore}
				onBatchPermanentDelete={onBatchPermanentDelete}
				onClearSelection={onClearSelection}
				onLoadMore={onLoadMorePlays}
				hasMore={hasMorePlays}
				isLoadingMore={isLoadingPlays}
			/>
		)
	}

	if (activeTab === 'formations') {
		return (
			<FormationsTab
				playbookId={playbookId}
				formations={formations}
				isLoading={isLoadingFormations}
				activeTab={activeTab}
				onTabChange={onTabChange}
				tabOptions={EDITOR_TAB_OPTIONS}
				onNewFormation={onNewFormation}
				onEditFormation={onEditFormation}
				onDeleteFormation={onDeleteFormation}
				onDuplicateFormation={onDuplicateFormation}
				onLoadMore={onLoadMoreFormations}
				hasMore={hasMoreFormations}
				isLoadingMore={isLoadingFormations}
			/>
		)
	}

	if (CONCEPT_CATEGORY_TABS.includes(activeTab)) {
		return (
			<ConceptCategoryTab
				playbookId={playbookId}
				activeTab={activeTab}
				onTabChange={onTabChange}
				onDeleteConcept={onDeleteConcept}
			/>
		)
	}

	if (activeTab === 'modifiers') {
		return (
			<ModifiersTab
				playbookId={playbookId}
				activeTab={activeTab}
				onTabChange={onTabChange}
			/>
		)
	}

	return null
}
